import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type {
  DrivingTestsStudentItemMs,
  DrivingTestsStudentItemRe,
} from 'teacher-ui-client/components/giraffe/charts/driving-tests/student'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface DrivingTestsStudentResponseRe {
  driving_tests_events_summary: {
    data: [{ category: 'completed_count'; count: number }, { category: 'percent_complete'; count: number }]
    driving_test_grade: null
  }[]
  driving_tests_events: {
    events: {
      category: 'letters_and_sounds' | 'content_words' | 'sight_words'
      completed: number
      total_attempts: number
      percent_complete: number
    }[]
    driving_test_grade: null
  }[]
}

export async function drivingTestsStudentRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<DrivingTestsStudentItemRe>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('driving_tests_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<DrivingTestsStudentResponseRe>(url, query)
  if (!data) return null

  const { driving_tests_events_summary: summaryData, driving_tests_events: eventData } = data
  if (summaryData.length === 0 || eventData.length === 0) return null

  const summary = summaryData[0]
  const completed_count = summary.data.find((summaryItem) => summaryItem.category === 'completed_count')!.count
  const percent_complete = summary.data.find((summaryItem) => summaryItem.category === 'percent_complete')!.count

  return {
    summary: {
      completed_count,
      percent_complete,
    },
    categories: eventData[0].events,
  }
}

export interface DrivingTestsStudentResponseMs {
  driving_tests_events_summary: SummaryData[]
  driving_tests_events: EventData[]
}

interface SummaryData {
  data: [{ category: 'completed_count'; count: number }, { category: 'percent_complete'; count: number }]
  driving_test_grade: number
}

interface EventData {
  events: {
    category: 'geometry' | 'measurement' | 'number' | 'operations' | 'patterns' | 'data'
    completed: number
    total_attempts: number
    percent_complete: number
  }[]
  driving_test_grade: number
}

export async function drivingTestsStudentMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<DrivingTestsStudentItemMs[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('driving_tests_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<DrivingTestsStudentResponseMs>(url, query)
  if (!data) return null

  const { driving_tests_events_summary: summaryData, driving_tests_events: eventsData } = data
  if (summaryData.length === 0 || eventsData.length === 0) return null

  return groupByGrade(summaryData, eventsData)
}

function groupByGrade(summaryData: SummaryData[], eventData: EventData[]) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.driving_test_grade - b.driving_test_grade)
  const sortedEventData = [...eventData].sort((a, b) => a.driving_test_grade - b.driving_test_grade)

  return sortedSummaryData.map((summary, index) => {
    const completed_count = summary.data.find((summaryItem) => summaryItem.category === 'completed_count')!.count
    const percent_complete = summary.data.find((summaryItem) => summaryItem.category === 'percent_complete')!.count

    return {
      driving_test_grade: summary.driving_test_grade,
      summary: {
        completed_count,
        percent_complete,
      },
      categories: sortedEventData[index].events,
    }
  })
}
